/**
 * Workspace Build Defaults API Client
 * Tier 1 (System/Workspace) of the 3-tier build configuration hierarchy.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DevSettingsUpdate } from './dev-settings';

const WORKSPACES_API_BASE = '/api/workspaces';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

/** Workspace build defaults — same shape as DevSettings but all fields are optional overrides */
export type WorkspaceBuildDefaults = DevSettingsUpdate;

export async function getWorkspaceBuildDefaults(workspaceId: string): Promise<WorkspaceBuildDefaults> {
  return request<WorkspaceBuildDefaults>(`${WORKSPACES_API_BASE}/${workspaceId}/build-defaults`);
}

export async function updateWorkspaceBuildDefaults(
  workspaceId: string,
  defaults: WorkspaceBuildDefaults,
): Promise<WorkspaceBuildDefaults> {
  return request<WorkspaceBuildDefaults>(`${WORKSPACES_API_BASE}/${workspaceId}/build-defaults`, {
    method: 'PATCH',
    body: JSON.stringify(defaults),
  });
}

export function useWorkspaceBuildDefaults(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-build-defaults', workspaceId],
    queryFn: () => getWorkspaceBuildDefaults(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60000,
  });
}

export function useUpdateWorkspaceBuildDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, defaults }: { workspaceId: string; defaults: WorkspaceBuildDefaults }) =>
      updateWorkspaceBuildDefaults(workspaceId, defaults),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-build-defaults', variables.workspaceId] });
      // Also invalidate all dev-settings since they inherit from workspace
      queryClient.invalidateQueries({ queryKey: ['dev-settings'] });
    },
  });
}
